import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { ControlText, ErrorPopover } from "../../helpers";
import { ControlSelect2 } from "../../helpers/ControlSelect2";
import { DualSelect } from "../../helpers/DualSelect";
import { ControlSmallWrapper } from "../../helpers/ControlSmallWrapper";
import { ModalActividadesCreateParticipante } from "../../modals/ModalActividadesCreateParticipante";
import { AbmForm } from "../AbmForm";

const isEqual = require("react-fast-compare");

/**
 * Formulario unificado de indicadores de una actividad.
 *
 * No tiene campos obligatorios: la carga de indicadores puede guardarse
 * parcialmente. La validación de completitud pertenece al cierre definitivo
 * de la actividad, no a este formulario.
 */
const schema = yup.object().shape({});

export const FormIndicadores = React.forwardRef(({
  item = {},
  onConfirm,
  onCancel,
  errorState,
  data = {}
}, ref) => {
  return (
    <AbmForm
      ref={ref}
      item={item}
      schema={schema}
      onSubmit={onConfirm}
      onCancel={onCancel}
      errorState={errorState}
      actionsBottom={false}
    >
      {({onControlChange, onObjectChange, entity, entityErrors}) => (
        <AbmFormContent
          onControlChange={onControlChange}
          onObjectChange={onObjectChange}
          entity={entity}
          entityErrors={entityErrors}
          data={data}
        />
      )}
    </AbmForm>
  );
});

const AbmFormContent = ({onControlChange, onObjectChange, entity, entityErrors, data}) => {

  const [showParticipante, setShowParticipante] = useState(false);
  const [participantes, setParticipantes] = useState([]);
  const participantesRef = useRef();

  useEffect(() => {
    if (entity?.participantes) {
      setParticipantes(current =>
        isEqual(entity.participantes, current) ? current : entity.participantes
      );
    }
  }, [entity?.participantes]);

  useEffect(() => {
    onControlChange({
      target: {
        name: "participantes",
        value: participantes
      }
    });
  }, [participantes]);

  const handleEnfoquePrincipalChange = (event) => {
    onControlChange(event);

    // Los secundarios dependen del principal. Si cambia o se elimina,
    // se descarta la selección anterior.
    onControlChange({
      target: {
        name: "id_enfoques_secundarios",
        value: []
      }
    });
  };

  const handleParticipante = (participante) => {
    setParticipantes(current => [...current, participante]);
    setShowParticipante(false);
  };

  const removeParticipante = (event, participante) => {
    event.preventDefault();

    const index = participantes.indexOf(participante);

    if (!participante.id) {
      setParticipantes(current =>
        current.filter((item, itemIndex) => itemIndex !== index)
      );
    } else {
      setParticipantes(current =>
        current.map((item, itemIndex) =>
          itemIndex !== index ? item : {...item, remove: true}
        )
      );
    }
  };

  const getOptionLabel = (options, value) => {
    return options?.find(
      option => parseInt(option.value) === parseInt(value)
    )?.label || "-";
  };

  const enfoquesSecundarios = data?.enfoques?.filter(
    enfoque => parseInt(enfoque.value) !== parseInt(entity?.id_enfoque_principal)
  );

  const participantesVisibles = participantes.filter(item => !item.remove);

  return (
    <>
      <ModalActividadesCreateParticipante
        showState={[showParticipante, setShowParticipante]}
        updateItem={handleParticipante}
        data={data}
      />

      <Grid item xs={12}>
        <DualSelect
          principal="id_categoria"
          secundario="id_categoria_secundaria"
          label="Categoría"
          errors={entityErrors}
          value={entity}
          onChange={onObjectChange}
          options={data?.categoriasActividad || []}
          Wrapper={ControlSmallWrapper}
        />
      </Grid>

      <Grid item xs={12}>
        <DualSelect
          principal="id_ubicacion"
          secundario="id_ubicacion_secundaria"
          label="Ubicación"
          errors={entityErrors}
          value={entity}
          onChange={onObjectChange}
          options={data?.ubicaciones || []}
          Wrapper={ControlSmallWrapper}
        />
      </Grid>

      <Grid item xs={12}>
        <DualSelect
          principal="id_formato"
          secundario="id_formato_secundario"
          label="Formato"
          errors={entityErrors}
          value={entity}
          onChange={onObjectChange}
          options={data?.formatos || []}
          Wrapper={ControlSmallWrapper}
        />
      </Grid>

      <Grid item xs={12}>
        <DualSelect
          principal="id_vinculo"
          secundario="id_vinculo_secundario"
          label="Vínculo"
          errors={entityErrors}
          value={entity}
          onChange={onObjectChange}
          options={data?.vinculos || []}
          Wrapper={ControlSmallWrapper}
        />
      </Grid>

      <Grid item xs={12}>
        <DualSelect
          principal="id_linea_estrategica"
          secundario="id_linea_estrategica_secundaria"
          label="Línea estratégica"
          errors={entityErrors}
          value={entity}
          onChange={onObjectChange}
          options={data?.lineasEstrategicas || []}
          Wrapper={ControlSmallWrapper}
        />
      </Grid>

      <Grid item xs={12}>
        <DualSelect
          principal="id_publico_prioritario"
          secundario="id_publico_prioritario_secundario"
          label="Público prioritario"
          errors={entityErrors}
          value={entity}
          onChange={onObjectChange}
          options={data?.publicosPrioritarios || []}
          Wrapper={ControlSmallWrapper}
        />
      </Grid>

      <Grid item xs={12}>
        <ControlSelect2
          name="id_enfoque_principal"
          label="Enfoque principal"
          errors={entityErrors}
          data={entity}
          onChange={handleEnfoquePrincipalChange}
          options={data?.enfoques || []}
          isMulti={false}
          closeMenuOnSelect={true}
          Wrapper={ControlSmallWrapper}
        />
      </Grid>

      <Grid item xs={12}>
        <ControlSelect2
          name="id_enfoques_secundarios"
          label="Enfoques secundarios"
          errors={entityErrors}
          data={entity}
          onChange={onControlChange}
          options={enfoquesSecundarios || []}
          isMulti={true}
          isDisabled={!entity?.id_enfoque_principal}
          closeMenuOnSelect={true}
          Wrapper={ControlSmallWrapper}
        />
      </Grid>

      <Grid item xs={12}>
        <ControlSelect2
          name="id_colectivos"
          label="Colectivos"
          errors={entityErrors}
          data={entity}
          onChange={onControlChange}
          options={data?.colectivos || []}
          isMulti={true}
          closeMenuOnSelect={true}
          Wrapper={ControlSmallWrapper}
        />
      </Grid>

      <Grid item xs={12}>
        <ControlText
          name="cantidad_participantes"
          label="Cantidad de participantes"
          type="number"
          errors={entityErrors}
          data={entity}
          onChange={onControlChange}
          Wrapper={ControlSmallWrapper}
          errorPlacement="bottom"
        />
      </Grid>

      <Grid item xs={12} ref={participantesRef}>
        <ControlSmallWrapper label="Estadística de participantes">
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Categoría</TableCell>
                  <TableCell>Género</TableCell>
                  <TableCell>Origen</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {participantesVisibles.map((participante, index) =>
                  <TableRow key={participante.id || index}>
                    <TableCell>{getOptionLabel(data?.categorias, participante.id_categoria)}</TableCell>
                    <TableCell>{getOptionLabel(data?.generos, participante.id_genero)}</TableCell>
                    <TableCell>{getOptionLabel(data?.origenes, participante.id_origen)}</TableCell>
                    <TableCell align="right">{participante.cantidad}</TableCell>
                    <TableCell>
                      <a href="javascript:" onClick={event => removeParticipante(event, participante)}>
                        Eliminar
                      </a>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell colSpan="5">
                    {participantesVisibles.length === 0 &&
                      <>No se cargaron registros estadísticos aún, </>
                    }
                    <a href="javascript:" onClick={event => {
                      event.preventDefault();
                      setShowParticipante(true);
                    }}>
                      {participantesVisibles.length === 0 ? "haga click aquí para agregar uno" : "Agregar registro"}
                    </a>
                    .
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </ControlSmallWrapper>
      </Grid>

      <ErrorPopover
        anchorEl={participantesRef.current}
        name="participantes"
      />
    </>
  );
};
